import React, { useMemo, useState, useCallback } from 'react';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridColumnGroupingModel,
} from '@mui/x-data-grid';
import { MuiButton, MuiSwitch, MuiDrawer } from '@ace/ace-ui-web';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import overallStyles from './Overall.module.css';
import pagesStandardStyles from '../../../Pages.module.css';
import performanceStyles from '../../Performance.module.css';
import {
  formatNumberWithCommas,
  formatLoadDate,
  isValidApiDate,
  isMobileDevice,
  getAlternatingRowClassName,
  renderEmptyValue,
  renderPeerGroupRankCell,
  formatNegativeChangeValue,
  isSmallNonZeroValue,
  formatSmallChangeValue,
  changeColumnColorStatus,
  minWidthForNumber,
} from '../../../../utils/appUtils';
import { renderPercentCell } from '../../../../utils/dataGridRender';
import { kpiContentMap } from '../../../../utils/viewInfoUtils';
import lang from '../../../../languages/en.json';
import { getDataGridStyles } from '../../../GridStyles';
import { getGroupColumnHeaderStyles } from '../../../GroupColumnHeaderGridStyles';
import Graph from './graphs/Graph';
import { useAppSelector } from '../../../../redux/hooks';
import { useBuildDynamicUrl } from '../../../../hooks/useDynamicUrl';
import { useClickHandler } from '../../../../utils/usageMetricsLogger';
import type {
  OverallMetrics,
  LineGraphModel,
  ValueFormat,
} from '../../../../models/LineGraphModels';
import SkeletonDataGrid from '../../../../components/skeletons/common/SkeletonDataGrid';
import {
  selectOverallLoading,
  selectOverallPerformanceData,
} from '../../../../redux/PerformanceReducer/Overall/overallSlice';
// Access global state to determine if multi-store features are enabled
import { selectIsMultiStoreEnabled } from '../../../../redux/UserReducer/userSlice';

// ─── Props interface ─────────────────────────────────────────────────────────

/** When set, only the specified column group is shown (used by print view to stack groups vertically) */
export type OverallVisibleGroup = 'total' | 'store' | 'ecom';

interface OverallProps {
  /** Pre-fetched data for print view — skips Redux read when provided */
  data?: OverallMetrics | null;
  /** When true, hides interactive elements (peer group toggle, buttons, drawer, links, graphs) */
  isPrintView?: boolean;
  /** Show only a single column group — used to render 3 stacked grids in print view */
  visibleGroup?: OverallVisibleGroup;
}

const BASE_URL_TYPE = 'WFC';
const PAGE_NM = 'Performance';
const TAB_NM = 'Overall Performance';
// KPI code type for type safety
type KpiCode = keyof OverallMetrics;

// Row interface for DataGrid - includes KPI codes for each column group
interface GridRow {
  id: number;
  fixedColumn: string;
  format: ValueFormat;
  decimalPlaces: number;
  totalKpi: KpiCode;
  inStoreKpi: KpiCode;
  ecomKpi: KpiCode;
}

interface LinkColumnConfig {
  label: string;
  links: Array<{ key: string; text: string }>;
}

// Core formatting function - handles all format types in one place
const formatCellValue = (
  value: number,
  format: ValueFormat,
  decimalPlaces: number,
  isChangeColumn: boolean,
): string => {
  // Handle small non-zero change values that would round to 0
  if (isChangeColumn && isSmallNonZeroValue(value, decimalPlaces)) {
    return formatSmallChangeValue(value, format, decimalPlaces);
  }

  const fixedValue = value.toFixed(decimalPlaces);

  if (format === 'DOL') {
    return isChangeColumn
      ? formatNegativeChangeValue(fixedValue, true)
      : `$${formatNumberWithCommas(fixedValue)}`;
  }
  if (format === 'PCT') {
    return isChangeColumn ? `${fixedValue}` : `${fixedValue}%`;
  }
  // VAL format
  return isChangeColumn
    ? formatNegativeChangeValue(fixedValue, false)
    : formatNumberWithCommas(fixedValue);
};
// Factory function to create renderCell functions for different column types (value, change value, change percent)
const createRenderCell = (formatter: (value: number, row: GridRow) => string | JSX.Element) => {
  return (params: GridRenderCellParams<GridRow, number>) => {
    const emptyCheck = renderEmptyValue(params.value);
    if (emptyCheck) return emptyCheck;
    return formatter(params.value, params.row);
  };
};

// Maps a numeric value to a color status string
const getNumericStatus = (value: number): string => {
  if (value > 0) return 'positive';
  if (value < 0) return 'negative';
  return 'neutral';
};

// Helper function to format numeric values based on format type and row-specific decimal places
const renderFormatCell = createRenderCell((value, row) =>
  formatCellValue(value, row.format, row.decimalPlaces, false),
);

// Helper function to render change value cells with color based on value and row-specific precision
const renderChangeValueCell = createRenderCell((value, row) => {
  const formattedText = formatCellValue(value, row.format, row.decimalPlaces, true);
  return (
    <span style={{ color: changeColumnColorStatus(getNumericStatus(value)) }}>{formattedText}</span>
  );
});

// Helper function to render change percent cells with color (always percentage format with 1 decimal)
const renderChangePercentNumericCell = createRenderCell(value => {
  const formattedText = isSmallNonZeroValue(value, 1)
    ? `${formatSmallChangeValue(value, 'PCT', 1)}`
    : `${formatNumberWithCommas(value.toFixed(1))}%`;
  return (
    <span style={{ color: changeColumnColorStatus(getNumericStatus(value)) }}>{formattedText}</span>
  );
});

const Overall: React.FC<OverallProps> = ({ data: dataProp, isPrintView = false, visibleGroup }) => {
  // Get performance data directly from Redux - no transformation needed
  const reduxData = useAppSelector(selectOverallPerformanceData);
  const reduxLoading = useAppSelector(selectOverallLoading);
  const isMultiStoreEnabled = useAppSelector(selectIsMultiStoreEnabled);

  // Use prop data (print view) or Redux data (live view)
  const performanceData = dataProp ?? reduxData;
  const isLoading = isPrintView ? false : reduxLoading;

  const buildDynamicUrl = useBuildDynamicUrl();
  const handleAndLogClick = useClickHandler(PAGE_NM, TAB_NM);

  const [showPeerGroupColumns, setShowPeerGroupColumns] = useState<boolean>(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [drawerContent, setDrawerContent] = useState<{
    title: string;
    content: React.ReactNode;
  }>({
    title: '',
    content: null,
  });

  const linkColumns: LinkColumnConfig[] = useMemo(
    () => [
      {
        label: lang.overallPerformanceContent.storeAnalysis,
        links: [
          {
            key: 'STR_YOY_CAL',
            text: lang.overallPerformanceContent.yoyViewCalendar,
          },
          {
            key: 'STR_YOY_FISCAL',
            text: lang.overallPerformanceContent.yoyViewFiscal,
          },
        ],
      },
      {
        label: lang.overallPerformanceContent.chainAnalysis,
        links: [
          {
            key: 'CHAIN_YOY_CAL',
            text: lang.overallPerformanceContent.chainYoyViewCalendar,
          },
          {
            key: 'CHAIN_YOY_FISCAL',
            text: lang.overallPerformanceContent.chainYoyViewFiscal,
          },
        ],
      },
      {
        label: lang.overallPerformanceContent.chainAnalysisByStore,
        links: [
          {
            key: 'CHAIN_STR_YOY_CAL',
            text: lang.overallPerformanceContent.chainYoyViewByStoreCalendar,
          },
          {
            key: 'CHAIN_STR_YOY_FISCAL',
            text: lang.overallPerformanceContent.chainYoyViewByStoreFiscal,
          },
        ],
      },
    ],
    [],
  );

  const PREV_RESULTS_BUTTONS = [
    { key: 'PREV_MO', label: lang.ecommerceContent.prevMonthEndResults },
    { key: 'PREV_QTR', label: lang.ecommerceContent.prevQtrEndResults },
    { key: 'PREV_YR', label: lang.ecommerceContent.prevYearEndResults },
  ];

  // Group header colors keyed by groupId for single-group mode
  const GROUP_STYLE_MAP: Record<string, { bg: string; color: string; border: string }> = {
    'total-group': { bg: '#333333', color: '#ffffff', border: '2px solid #757575' },
    'store-group': { bg: '#feeded', color: '#000', border: '2px solid #757575' },
    'ecom-group': { bg: '#e5f7fd', color: '#000', border: '1px solid #e0e0e0' },
  };

  // nth-of-type indices when all 3 groups are rendered together
  const COLUMN_GROUP_STYLES = [
    { index: 3, ...GROUP_STYLE_MAP['total-group'] },
    { index: 4, ...GROUP_STYLE_MAP['store-group'] },
    { index: 5, ...GROUP_STYLE_MAP['ecom-group'] },
  ];

  const reportName = lang.pageContent.customizeReport;

  const handleCustomizeReportClick = useCallback((): void => {
    const url = buildDynamicUrl(BASE_URL_TYPE, 'OVR_CUSTOM_REPORT');
    handleAndLogClick('Overall Performance - Customize Report', {
      url,
      urlTarget: '_blank',
    });
  }, [buildDynamicUrl, handleAndLogClick]);

  const getColumnGroupHeaderStyles = () => {
    // When rendering a single group, there's only one group header (always at nth-of-type(3)).
    // Use the correct color for the active group instead of relying on index.
    if (visibleGroup) {
      const groupId = `${visibleGroup}-group`;
      const style = GROUP_STYLE_MAP[groupId];
      if (style) {
        return {
          '& .MuiDataGrid-columnHeader--filledGroup:nth-of-type(3)': {
            backgroundColor: style.bg,
            color: style.color,
            borderRight: style.border,
          },
        };
      }
    }

    // All groups visible — use index-based selectors
    return COLUMN_GROUP_STYLES.reduce(
      (acc, { index, bg, color, border }) => ({
        ...acc,
        [`& .MuiDataGrid-columnHeader--filledGroup:nth-of-type(${index})`]: {
          backgroundColor: bg,
          color,
          borderRight: border,
        },
      }),
      {},
    );
  };

  // Helper function to get KPI data from row based on column group
  const getKpiFromRow = useCallback(
    (row: GridRow, columnName: string): LineGraphModel | undefined => {
      let kpiCode: KpiCode;
      if (columnName === 'total') {
        kpiCode = row.totalKpi;
      } else if (columnName === 'store') {
        kpiCode = row.inStoreKpi;
      } else {
        kpiCode = row.ecomKpi;
      }
      return performanceData?.[kpiCode];
    },
    [performanceData],
  );

  // Row data with KPI codes for each column group (total, inStore, ecommerce)
  const rowData = useMemo(
    (): GridRow[] => [
      {
        id: 1,
        fixedColumn: lang.pageContent.sales,
        format: 'DOL',
        decimalPlaces: 0,
        totalKpi: 'TSALE',
        inStoreKpi: 'SALES',
        ecomKpi: 'ESALE',
      },
      {
        id: 2,
        fixedColumn: lang.pageContent.grossProfit,
        format: 'DOL',
        decimalPlaces: 0,
        totalKpi: 'TGP',
        inStoreKpi: 'GP',
        ecomKpi: 'EGP',
      },
      {
        id: 3,
        fixedColumn: lang.pageContent.grossMargin,
        format: 'PCT',
        decimalPlaces: 1,
        totalKpi: 'TGM',
        inStoreKpi: 'GM',
        ecomKpi: 'EGM',
      },
      {
        id: 4,
        fixedColumn: lang.pageContent.transactions,
        format: 'VAL',
        decimalPlaces: 0,
        totalKpi: 'TTCNT',
        inStoreKpi: 'TCNT',
        ecomKpi: 'ECNT',
      },
      {
        id: 5,
        fixedColumn: lang.overallPerformanceContent.avgTrans,
        format: 'DOL',
        decimalPlaces: 2,
        totalKpi: 'TATS',
        inStoreKpi: 'ATS',
        ecomKpi: 'EATS',
      },
    ],
    [],
  );

  // Helper function to create column group with valueGetter
  // Wrapped in useCallback to maintain stable reference for useMemo dependencies
  const createColumnGroup = useCallback(
    (columnName: string): GridColDef[] => {
      // Find the largest absolute value across all rows for a given field.
      // Used by minWidthForNumber to compute a column width that won't truncate any value.
      const maxAbs = (getValue: (row: GridRow) => number | null | undefined): number =>
        Math.max(0, ...rowData.map(row => Math.abs(getValue(row) ?? 0)));

      const printMinWidthWhenVisible = showPeerGroupColumns ? 115 : 150;
      const printMinWidth = visibleGroup ? printMinWidthWhenVisible : 82;

      const columnConfig = {
        flex: 1,
        minWidth: 115.2,
        headerClassName: pagesStandardStyles.columnHeader,
        align: 'right' as const,
      };

      const createColumn = (
        fieldSuffix: string,
        headerName: string,
        minWidth: number,
        valueGetter: GridColDef['valueGetter'],
        renderCell: GridColDef['renderCell'],
        cellClassName?: GridColDef['cellClassName'],
      ): GridColDef => ({
        field: `${columnName}${fieldSuffix}`,
        headerName,
        ...columnConfig,
        minWidth,
        valueGetter,
        renderCell,
        ...(cellClassName && { cellClassName }),
      });

      const createPeerGroupColumns = (
        columnName: string,
        suffix: string,
        benchmarkRenderer: GridColDef['renderCell'],
        benchmarkMinWidth = 115.2,
      ): GridColDef[] =>
        showPeerGroupColumns
          ? [
              {
                field: `${columnName}PeerGroupRank${suffix}`,
                // Change header text to MS based on multi-store status
                headerName: isMultiStoreEnabled
                  ? lang.pageContent.msGroupRank
                  : lang.pageContent.peerGroupRank,
                ...columnConfig,
                valueGetter: (_, row) =>
                  suffix === ''
                    ? getKpiFromRow(row, columnName)?.ytdAmountRank ?? null
                    : getKpiFromRow(row, columnName)?.ytdDifferenceRank ?? null,
                renderCell: renderPeerGroupRankCell,
              },
              {
                field: `${columnName}Benchmark${suffix}`,
                headerName: lang.pageContent.percentBenchmark,
                ...columnConfig,
                minWidth: benchmarkMinWidth,
                valueGetter: (_, row) =>
                  suffix === ''
                    ? getKpiFromRow(row, columnName)?.ytdBenchmarkAmount ?? null
                    : getKpiFromRow(row, columnName)?.ytdBenchmarkPercent ?? null,
                renderCell: benchmarkRenderer,
                // Custom header to include benchmark explanation tooltip for multi-store mode
                ...(isMultiStoreEnabled && {
                  renderHeader: () => (
                    <div className={pagesStandardStyles.benchmarkHeader}>
                      <span>80%</span>
                      <span className={pagesStandardStyles.benchmarkHeaderRow}>
                        {lang.pageContent.benchmark}
                        <Tooltip
                          title={lang.pageContent.benchmarkPerStoreAveragesNote}
                          arrow
                          enterTouchDelay={0}
                          leaveTouchDelay={6000}>
                          <InfoOutlinedIcon
                            fontSize="small"
                            className={pagesStandardStyles.benchmarkInfoIconHeader}
                          />
                        </Tooltip>
                      </span>
                    </div>
                  ),
                }),
              },
            ]
          : [];

      return [
        createColumn(
          'CurrentYear',
          lang.pageContent.currentYear,
          isPrintView
            ? printMinWidth
            : minWidthForNumber(
                maxAbs(row => getKpiFromRow(row, columnName)?.currentYtdAmount),
                '$',
              ),
          (_, row) => getKpiFromRow(row, columnName)?.currentYtdAmount ?? null,
          renderFormatCell,
        ),
        ...createPeerGroupColumns(
          columnName,
          '',
          renderFormatCell,
          isPrintView
            ? printMinWidth
            : minWidthForNumber(
                maxAbs(row => getKpiFromRow(row, columnName)?.currentYtdAmount),
                '$',
              ),
        ),
        createColumn(
          'LastYear',
          lang.pageContent.lastYear,
          isPrintView
            ? printMinWidth
            : minWidthForNumber(
                maxAbs(row => getKpiFromRow(row, columnName)?.priorYtdAmount),
                '$',
              ),
          (_, row) => getKpiFromRow(row, columnName)?.priorYtdAmount ?? null,
          renderFormatCell,
        ),
        createColumn(
          'ChangeValue',
          lang.pageContent.changeOverLastYear,
          isPrintView
            ? printMinWidth
            : minWidthForNumber(
                maxAbs(row => getKpiFromRow(row, columnName)?.differenceAmount),
                '-$',
              ),
          (_, row) => getKpiFromRow(row, columnName)?.differenceAmount ?? null,
          renderChangeValueCell,
        ),
        createColumn(
          'ChangePercent',
          lang.pageContent.changeOverLastPercent,
          isPrintView ? printMinWidth : 115.2,
          (_, row) => getKpiFromRow(row, columnName)?.differencePercent ?? null,
          renderChangePercentNumericCell,
        ),
        ...createPeerGroupColumns(
          columnName,
          '2',
          renderPercentCell,
          isPrintView ? printMinWidth : 115.2,
        ),
      ];
    },
    [showPeerGroupColumns, getKpiFromRow, rowData, isPrintView, visibleGroup, isMultiStoreEnabled],
  );

  const totalColumns = useMemo(() => createColumnGroup('total'), [createColumnGroup]);
  const storeColumns = useMemo(() => createColumnGroup('store'), [createColumnGroup]);
  const ecomColumns = useMemo(() => createColumnGroup('ecom'), [createColumnGroup]);

  // Get last column field names for groups
  // Used in DataGrid sx prop for styling borders
  const lastGroupOneField = useMemo(
    () => totalColumns[totalColumns.length - 1].field,
    [totalColumns],
  );
  // Optional second group
  const lastGroupTwoField = useMemo(
    () => storeColumns[storeColumns.length - 1].field,
    [storeColumns],
  );

  // Map group name → column set for filtering
  const groupColumnsMap: Record<string, GridColDef[]> = useMemo(
    () => ({ total: totalColumns, store: storeColumns, ecom: ecomColumns }),
    [totalColumns, storeColumns, ecomColumns],
  );

  const columns: GridColDef[] = useMemo(() => {
    const fixedCol: GridColDef = {
      field: 'fixedColumn',
      headerName: lang.pageContent.metric,
      width: 135,
      headerClassName: pagesStandardStyles.columnHeader,
      cellClassName: pagesStandardStyles.stickyColumn,
    };

    // When visibleGroup is set, show only that group's columns
    if (visibleGroup) {
      return [fixedCol, ...(groupColumnsMap[visibleGroup] ?? [])];
    }

    return [
      fixedCol,
      // Total group
      ...totalColumns,
      // In Store group
      ...storeColumns,
      // Ecommerce group
      ...ecomColumns,
    ];
  }, [totalColumns, storeColumns, ecomColumns, visibleGroup, groupColumnsMap]);

  // All possible group configs
  const allGroupConfigs = useMemo(
    () => [
      {
        key: 'total' as const,
        groupId: 'total-group',
        headerName: lang.overallPerformanceContent.total,
        columns: totalColumns,
      },
      {
        key: 'store' as const,
        groupId: 'store-group',
        headerName: lang.overallPerformanceContent.inStore,
        columns: storeColumns,
      },
      {
        key: 'ecom' as const,
        groupId: 'ecom-group',
        headerName: lang.overallPerformanceContent.ecommerceLowerCase,
        columns: ecomColumns,
      },
    ],
    [totalColumns, storeColumns, ecomColumns],
  );

  // Define column grouping model for MUI DataGrid
  const columnGroupingModel: GridColumnGroupingModel = useMemo(() => {
    const groups = visibleGroup
      ? allGroupConfigs.filter(g => g.key === visibleGroup)
      : allGroupConfigs;

    return groups.map(g => ({
      groupId: g.groupId,
      headerName: g.headerName,
      children: g.columns.map(col => ({ field: col.field })),
    }));
  }, [allGroupConfigs, visibleGroup]);

  const errorMessage = lang.pageContent.dataUnavailableError.replace(
    '{section}',
    lang.overallPerformanceContent.title,
  );

  const renderDataGridContent = () => {
    if (isLoading) {
      return (
        <SkeletonDataGrid
          tabCount={0}
          rowCount={0}
          columnCount={25}
        />
      );
    } else if (performanceData == null) {
      if (isPrintView) return null;
      return (
        <div className={pagesStandardStyles.errorContent}>
          <p>{errorMessage}</p>
        </div>
      );
    }
    return (
      <DataGrid
        // === REQUIRED DATA PROPS ===
        rows={rowData}
        columns={columns}
        columnGroupingModel={columnGroupingModel}
        // === STANDARD BEHAVIOR (Recommended for all grids) ===
        disableRowSelectionOnClick
        disableVirtualization
        disableColumnMenu
        disableColumnSorting
        disableColumnResize
        hideFooter
        autoHeight
        // === STYLING & LAYOUT ===
        className={pagesStandardStyles.dataGrid}
        getRowClassName={params =>
          getAlternatingRowClassName(
            params,
            pagesStandardStyles.evenRow,
            pagesStandardStyles.oddRow,
          )
        }
        // === CUSTOMIZABLE SETTINGS (Adjust per grid if needed) ===
        columnHeaderHeight={52}
        columnBufferPx={1500}
        // === CUSTOM STYLES (Grid-specific overrides) ===
        sx={{
          ...getColumnGroupHeaderStyles(),
          ...getGroupColumnHeaderStyles(
            lastGroupOneField,
            // Comment below to remove second group border styling
            lastGroupTwoField,
          ),
          ...getDataGridStyles('fixedColumn'),
        }}
      />
    );
  };

  if (isPrintView && !performanceData) {
    return null;
  }

  return (
    <div>
      {/* Page Header Title Section — hidden when rendering a single group slice (2nd/3rd instance) */}
      {!visibleGroup || visibleGroup === 'total' ? (
        <div className={pagesStandardStyles.pageHeader}>
          <div
            className={`${pagesStandardStyles.headerContainer} ${overallStyles.headerContainer}`}>
            <div>
              <h2 className={pagesStandardStyles.pageTitle}>
                {lang.overallPerformanceContent.title}
              </h2>
              <p className={pagesStandardStyles.pageSubtitle}>
                {lang.pageContent.datePrefix}{' '}
                {isValidApiDate(performanceData?.TSALE?.pointOfSaleDate?.toString())
                  ? formatLoadDate(performanceData.TSALE.pointOfSaleDate.toString())
                  : 'N/A'}
              </p>
            </div>
            {!isPrintView && (
              <div
                className={`${pagesStandardStyles.headerActions} ${overallStyles.headerActions}`}>
                {!isMobileDevice() && (
                  <MuiButton
                    colors="secondary"
                    iconPosition="right"
                    iconType="launch"
                    onClick={handleCustomizeReportClick}>
                    {reportName}
                  </MuiButton>
                )}
                <div className={pagesStandardStyles.switchWrapper}>
                  <MuiSwitch
                    key={showPeerGroupColumns.toString()}
                    color="primary"
                    defaultChecked={showPeerGroupColumns}
                    label={lang.pageContent.toggleShowPeerGroup}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setShowPeerGroupColumns(event.target.checked);
                    }}
                    size="medium"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* DataGrid with Column Grouping */}
      <div>
        {renderDataGridContent()}

        {/* Footer Disclaimer — only show on the last group or when showing all */}
        {(!visibleGroup || visibleGroup === 'ecom') && (
          <div className={pagesStandardStyles.disclaimer}>
            {lang.overallPerformanceContent.disclaimer}
          </div>
        )}

        {/* Print-only benchmark note below the Total grid (multi-store only) */}
        {isPrintView && isMultiStoreEnabled && visibleGroup === 'total' && (
          <div className={pagesStandardStyles.printBenchmarkNote}>
            <InfoOutlinedIcon
              fontSize="small"
              className={pagesStandardStyles.printBenchmarkNoteIcon}
            />
            <span>{lang.pageContent.benchmarkPerStoreAveragesNote}</span>
          </div>
        )}

        {/* Detailed Breakdown, Links, Drawer, Prev Results, Graphs, Dialog — hidden in print view */}
        {!isPrintView && (
          <>
            <div
              className={`${pagesStandardStyles.detailedBreakdownSection} ${overallStyles.detailedBreakdownSection}`}>
              <div className={pagesStandardStyles.detailedBreakdownHeader}>
                <span className={pagesStandardStyles.detailedBreakdownTitle}>
                  {lang.overallPerformanceContent.forDetailedBreakDown}
                </span>
                <a
                  href="#"
                  className={`${pagesStandardStyles.viewInfoLink} ${overallStyles.viewInfoLink}`}
                  onClick={e => {
                    e.preventDefault();
                    const contentInfo = kpiContentMap['OP_BREAKDOWN'];
                    if (contentInfo) {
                      const ContentComponent = contentInfo.component;
                      handleAndLogClick(
                        'Overall Performance - View Info - For Detailed Breakdown',
                        {
                          callback: () => {
                            setDrawerContent({
                              title: contentInfo.title || 'More Information',
                              content: <ContentComponent />,
                            });
                            setIsDrawerOpen(true);
                          },
                        },
                      );
                    }
                  }}>
                  {lang.pageContent.viewInfo}
                </a>
              </div>

              {/* Analysis Links Grid */}
              <div className={performanceStyles.linkGrid}>
                {linkColumns.map(column => (
                  <div
                    key={column.label}
                    className={performanceStyles.linkColumn}>
                    <div className={performanceStyles.linkLabel}>{column.label}</div>
                    {column.links.map(link => (
                      <a
                        key={link.text}
                        href={buildDynamicUrl(BASE_URL_TYPE, link.key) || undefined}
                        className={pagesStandardStyles.linkItem}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => {
                          e.preventDefault();
                          const url = buildDynamicUrl(BASE_URL_TYPE, link.key);
                          if (url) {
                            handleAndLogClick(`Overall Performance - ${link.text}`, {
                              url,
                              urlTarget: '_blank',
                            });
                          }
                        }}>
                        {link.text}
                      </a>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* MuiDrawer */}
            <MuiDrawer
              anchor="right"
              headerText={drawerContent.title}
              onClose={() => setIsDrawerOpen(false)}
              open={isDrawerOpen}>
              {drawerContent.content}
            </MuiDrawer>

            {/* Horizontal Divider */}
            <hr className={pagesStandardStyles.sectionDivider} />

            {/* Previous Results Buttons - Right Aligned */}
            {!isMobileDevice() && (
              <div className={pagesStandardStyles.buttonContainerRight}>
                {PREV_RESULTS_BUTTONS.map(button => (
                  <MuiButton
                    key={button.key}
                    colors="secondary"
                    onClick={() => {
                      const url = buildDynamicUrl(BASE_URL_TYPE, button.key);
                      handleAndLogClick(`Overall Performance - ${button.label}`, {
                        url,
                        urlTarget: '_blank',
                      });
                    }}>
                    {button.label}
                  </MuiButton>
                ))}
              </div>
            )}

            <div className={performanceStyles.container}>
              <Graph />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Overall;
